import { useCallback } from 'react';

export function useSlotAvailability({ state }) {
  const getGroundById = useCallback(
    (id) => state.grounds.find((ground) => ground.id === parseInt(id, 10)),
    [state.grounds]
  );

  const getServiceById = useCallback(
    (groundId, serviceId) => {
      const ground = getGroundById(groundId);
      if (!ground) return null;
      return ground.services.find((service) => service.id === parseInt(serviceId, 10));
    },
    [getGroundById]
  );

  const getAllServices = useCallback(
    () =>
      state.grounds.flatMap((ground) =>
        ground.services.map((service) => ({
          ...service,
          groundId: ground.id,
          groundName: ground.name,
          groundLocation: ground.location,
        }))
      ),
    [state.grounds]
  );

  const getSeasonById = useCallback((seasonId) => state.seasons.find((season) => season.id === seasonId), [state.seasons]);

  const getSlotDetails = useCallback(
    (groundId, serviceId, slotId) => {
      const service = getServiceById(groundId, serviceId);
      if (!service) return undefined;
      return service.dailySlots?.find((slot) => slot.id === slotId);
    },
    [getServiceById]
  );

  const calculateSlotPrice = useCallback(
    (groundId, serviceId, slotId, customPrice = null) => {
      const slot = getSlotDetails(groundId, serviceId, slotId);
      if (!slot) return 0;
      const basePrice = customPrice ?? slot.defaultPrice ?? 0;
      const currentMonth = new Date().getMonth() + 1;
      const season = currentMonth >= 3 && currentMonth <= 9 ? 'peak' : 'standard';
      const multiplier = getSeasonById(season)?.priceMultiplier ?? 1;
      return Math.round(basePrice * multiplier);
    },
    [getSlotDetails, getSeasonById]
  );

  const getSlotAvailability = useCallback(
    (date, groundId, serviceId) => {
      const dateKey = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      const service = getServiceById(groundId, serviceId);
      if (!service) return {};

      const configuredService = state.slotConfiguration?.services?.find(
        (sc) => parseInt(sc?.service?.id, 10) === parseInt(serviceId, 10)
      );
      const configSlots = configuredService?.service?.slots || configuredService?.slots || [];
      const dailySlots = Array.isArray(service.dailySlots)
        ? service.dailySlots
        : Array.isArray(service.slots)
          ? service.slots
          : Array.isArray(configSlots)
            ? configSlots
            : [];

      const availability = {};
      dailySlots.forEach((slot) => {
        const slotBookings = state.bookings.filter(
          (booking) =>
            booking.date === dateKey &&
            booking.groundId === parseInt(groundId, 10) &&
            booking.serviceId === parseInt(serviceId, 10) &&
            booking.slotId === slot.id &&
            booking.status !== 'cancelled'
        );

        if (service.type === 'cricket_match') {
          const hasBothTeamsBooking = slotBookings.some((b) => b.bookingType === 'both');
          if (slotBookings.length === 0) {
            availability[slot.id] = { status: 'available', color: 'green', bookings: [] };
          } else if (slotBookings.length === 1 && !hasBothTeamsBooking) {
            availability[slot.id] = { status: 'half-booked', color: 'amber', bookings: slotBookings };
          } else {
            availability[slot.id] = { status: 'booked', color: 'red', bookings: slotBookings };
          }
        } else {
          const maxCapacity = service.rules?.maxGuests || 6;
          const totalGuests = slotBookings.reduce((sum, b) => sum + (b.guestList?.length || 0), 0);
          if (totalGuests === 0) {
            availability[slot.id] = { status: 'available', color: 'green', bookings: [] };
          } else if (totalGuests < maxCapacity) {
            availability[slot.id] = { status: 'half-booked', color: 'amber', bookings: slotBookings };
          } else {
            availability[slot.id] = { status: 'booked', color: 'red', bookings: slotBookings };
          }
        }
      });

      return availability;
    },
    [state.bookings, state.slotConfiguration?.services, getServiceById]
  );

  return {
    getGroundById,
    getServiceById,
    getAllServices,
    getSeasonById,
    getSlotDetails,
    calculateSlotPrice,
    getSlotAvailability,
  };
}
